import React, { useEffect, useState } from "react";
import { ClipboardList, X } from "lucide-react";
import { toast } from "sonner";
import client, { apiErr } from "@/lib/api";

const FULFILL = {
  pending: ["قيد الانتظار", "var(--k-ink-soft)"], payment_confirmed: ["تم تأكيد الدفع", "var(--k-emerald)"],
  confirmed: ["مؤكّد", "var(--k-emerald)"], processing: ["قيد التجهيز", "var(--k-gold-deep)"],
  shipped: ["قيد الشحن", "var(--k-gold-deep)"], delivered: ["تم التسليم", "var(--k-emerald)"],
  cancelled: ["ملغي", "var(--k-ruby)"], disputed: ["نزاع", "var(--k-ruby)"],
};
const ALL_STATUS = Object.keys(FULFILL);

export default function AdminOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const loadOrders = () => client.get("/admin/orders", { params: { status: statusFilter, q: q || undefined } }).then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { const t = setTimeout(loadOrders, q ? 250 : 0); return () => clearTimeout(t); }, [statusFilter, q]);

  const openDetail = (orderId) => client.get(`/admin/orders/${orderId}`).then((r) => setDetail(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  const setStatus = async (orderId, status) => {
    try { await client.put(`/admin/orders/${orderId}/status`, { status }); toast.success("تم تحديث حالة الطلب"); loadOrders(); if (detail) openDetail(orderId); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div dir="rtl">
      <h2 className="font-head text-[16px] font-bold px-4 pt-5 pb-2 flex items-center gap-1.5"><ClipboardList size={17} style={{ color: "var(--k-gold-deep)" }} /> إدارة الطلبات ({orders.length})</h2>
      <div className="flex flex-wrap gap-2 px-4 mb-2">
        <input data-testid="order-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث برقم الطلب أو البريد أو الهاتف" className="flex-1 min-w-[180px] rounded-lg px-3 py-2.5 text-[13px]" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }} />
        <select data-testid="order-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg px-2 py-2.5 text-[13px]" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
          <option value="all">كل الحالات</option>
          {ALL_STATUS.map((s) => <option key={s} value={s}>{FULFILL[s][0]}</option>)}
        </select>
      </div>
      <div className="px-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5 items-start pb-6">
        {orders.length === 0 ? <p className="text-[13px] p-4 md:col-span-2 xl:col-span-3" style={{ color: "var(--k-ink-soft)" }}>لا توجد طلبات مطابقة</p> : orders.map((o) => {
          const [lbl, col] = FULFILL[o.status || "pending"] || FULFILL.pending;
          return (
            <button key={o.orderId} data-testid={`admin-order-${o.orderId}`} onClick={() => openDetail(o.orderId)} className="k-tap text-right rounded-2xl p-3.5" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
              <div className="flex justify-between items-center mb-1">
                <span className="font-num font-bold text-[13px]">{o.orderId}</span>
                <span className="text-[10.5px] font-bold px-2.5 py-1 rounded-full" style={{ color: col, background: "var(--k-paper)", border: "1px solid var(--k-line)" }}>{lbl}</span>
              </div>
              <p className="text-[11.5px]" style={{ color: "var(--k-ink-soft)" }}>{o.email} · <span className="font-num">{o.phone}</span></p>
              <div className="flex justify-between text-[12px] mt-1.5 font-bold"><span>الإجمالي</span><span className="font-num">{o.total} د.ل</span></div>
              <div className="flex justify-between text-[11px]" style={{ color: "var(--k-emerald)" }}><span>صافي التجار</span><span className="font-num">{o.netPayout ?? "—"} د.ل</span></div>
            </button>
          );
        })}
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: "rgba(18,32,58,.55)" }} onClick={() => setDetail(null)}>
          <div className="w-full max-w-[560px] max-h-[90vh] overflow-y-auto no-scrollbar rounded-t-3xl p-5 k-rise" style={{ background: "var(--k-paper)" }} onClick={(e) => e.stopPropagation()} data-testid="order-detail">
            <div className="flex justify-between items-center mb-3 sticky top-0 pb-2" style={{ background: "var(--k-paper)" }}>
              <h3 className="font-head text-[16px] font-bold">تفاصيل الطلب {detail.order.orderId}</h3>
              <button data-testid="order-detail-close" onClick={() => setDetail(null)} className="k-tap w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="rounded-xl p-3" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
                <p className="text-[11px] font-bold mb-1" style={{ color: "var(--k-gold-deep)" }}>المشتري</p>
                <p className="text-[12px] font-bold">{detail.buyer?.name || "—"}</p>
                <p className="text-[11px] font-num" style={{ color: "var(--k-ink-soft)" }}>{detail.buyer?.email}</p>
                <p className="text-[11px] font-num" style={{ color: "var(--k-ink-soft)" }}>{detail.order.phone}</p>
                <p className="text-[11px]" style={{ color: "var(--k-ink-soft)" }}>الوجهة: {detail.order.destinationCity || detail.order.city}</p>
              </div>
              <div className="rounded-xl p-3" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
                <p className="text-[11px] font-bold mb-1" style={{ color: "var(--k-gold-deep)" }}>التاجر / الانطلاق</p>
                {detail.merchants.map((m) => (
                  <div key={m.id} className="mb-1">
                    <p className="text-[12px] font-bold">{m.name}</p>
                    <p className="text-[11px] font-num" style={{ color: "var(--k-ink-soft)" }}>{m.phone} · {m.city}</p>
                    <p className="text-[11px]" style={{ color: "var(--k-emerald)" }}>صافي: {m.net} د.ل (عمولة {m.commission})</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl p-3 mb-3" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
              {detail.order.items.map((i, k) => (
                <div key={k} className="flex justify-between text-[12px] py-0.5"><span>{i.name} × {i.qty}</span><span className="font-num">{i.subtotal} د.ل</span></div>
              ))}
              <div className="flex justify-between text-[13px] font-bold pt-2 mt-1.5" style={{ borderTop: "1px solid var(--k-line)" }}><span>الإجمالي</span><span className="font-num">{detail.order.total} د.ل</span></div>
              <div className="flex justify-between text-[11.5px]" style={{ color: "var(--k-ink-soft)" }}><span>العمولة</span><span className="font-num">-{detail.order.commissionTotal ?? 0} د.ل</span></div>
              <div className="flex justify-between text-[12px]" style={{ color: "var(--k-emerald)" }}><span>صافي مستحقات التجار</span><span className="font-num font-bold">{detail.order.netPayout ?? 0} د.ل</span></div>
              <p className="text-[11px] mt-1.5" style={{ color: "var(--k-ink-soft)" }}>الدفع: {detail.order.payMethodLabel} ({detail.order.payStatus})</p>
            </div>
            {Array.isArray(detail.order.timeline) && (
              <div className="rounded-xl p-3 mb-3" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
                <p className="text-[11px] font-bold mb-1.5" style={{ color: "var(--k-gold-deep)" }}>السجل الزمني</p>
                {detail.order.timeline.map((t, k) => (
                  <div key={k} className="flex items-center gap-2 text-[11px] py-0.5" style={{ color: "var(--k-ink-soft)" }}>
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "var(--k-gold)" }} />
                    <span className="font-bold" style={{ color: "var(--k-ink)" }}>{FULFILL[t.status]?.[0] || t.status}</span>
                    <span className="font-num text-[10px]">{(t.at || "").slice(0, 16).replace("T", " ")}</span>
                    {t.note && <span>· {t.note}</span>}
                  </div>
                ))}
              </div>
            )}
            <p className="text-[12px] font-bold mb-1.5">تغيير الحالة</p>
            <div className="flex flex-wrap gap-1.5">
              {ALL_STATUS.filter((s) => s !== "disputed").map((s) => (
                <button key={s} data-testid={`order-set-${s}`} onClick={() => setStatus(detail.order.orderId, s)} className="k-tap px-3 py-2 rounded-lg text-[11.5px] font-bold" style={{ background: detail.order.status === s ? "var(--k-ink)" : "var(--k-surface)", color: detail.order.status === s ? "#fff" : "var(--k-ink)", border: "1px solid var(--k-line)" }}>{FULFILL[s][0]}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
